import { SelectField } from "@overmap-ai/forms"
import type { ReactNode } from "react"

import {
	NullableStringEqualsFilterModifier,
	NullableStringNotEqualsFilterModifier,
	StringArrayExcludesFilterModifier,
	StringArrayIncludesFilterModifier,
} from "../../modifiers"
import { BaseFilterDef, createFilterModifierConfig, type FilterDefRenderProps } from "../BaseFilterDef"
import { SelectFieldFilterCell } from "./SelectFieldFilterCell"

type SelectFieldValue = string | null
type SelectFilterValue = string[] | string | null

export class SelectFieldFilterDef extends BaseFilterDef<SelectFieldValue, SelectFilterValue> {
	readonly id: string
	readonly defaultFilterValue: SelectFilterValue | undefined = undefined
	readonly defaultFilterModifier: keyof typeof this.modifiers = "equals"

	readonly field: SelectField

	constructor(field: SelectField) {
		super()
		this.field = field
		this.id = field.id
	}

	readonly modifiers = {
		equals: createFilterModifierConfig({
			modifier: NullableStringEqualsFilterModifier,
			isValueValid: (_value: SelectFieldValue): _value is string | null => true,
			isFilterValueValid: (filterValue: SelectFilterValue): filterValue is string | null =>
				!Array.isArray(filterValue),
		}),
		notEquals: createFilterModifierConfig({
			modifier: NullableStringNotEqualsFilterModifier,
			isValueValid: (_value: SelectFieldValue): _value is string | null => true,
			isFilterValueValid: (filterValue: SelectFilterValue): filterValue is string | null =>
				!Array.isArray(filterValue),
		}),
		includes: createFilterModifierConfig({
			modifier: StringArrayIncludesFilterModifier,
			isValueValid: (value: SelectFieldValue): value is string => value !== null,
			isFilterValueValid: (filterValue: SelectFilterValue): filterValue is string[] => Array.isArray(filterValue),
		}),
		excludes: createFilterModifierConfig({
			modifier: StringArrayExcludesFilterModifier,
			isValueValid: (value: SelectFieldValue): value is string => value !== null,
			isFilterValueValid: (filterValue: SelectFilterValue): filterValue is string[] => Array.isArray(filterValue),
		}),
	}

	render(props: FilterDefRenderProps<SelectFieldFilterDef>): ReactNode {
		return <SelectFieldFilterCell key={props.filter.id} field={this.field} {...props} />
	}
}
